import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, SkeletonText, Spinner } from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';
import type { ComponentType } from 'react';

import { api } from '../api/client.js';
import { useMe } from '../auth/useAuth.js';
import { useModules } from '../modules/useModules.js';
import { ActivityFeedWidget, ModuleStatusWidget } from '../widgets/core.js';

interface ResolvedWidget {
  key: string;
  title: string;
  span: 1 | 2 | 3;
  Component: ComponentType;
}

/** „Mein Tag" gehört nach oben — bekannte Schlüssel steuern die Reihenfolge. */
const WIDGET_PRIORITY: Record<string, number> = {
  'projects.my_day': 0,
  'crm.pipeline_summary': 1,
  'core.activity_feed': 0,
  'accounting.finance_summary': 1,
  'projects.summary': 2,
  'core.modules': 9,
};

const DASHBOARD_VIEW = 'core.dashboard';

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 11) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

/**
 * Widget-Dashboard „Mein Tag": Core-Widgets + Widgets aller aktivierten
 * Module, pro Nutzer ein-/ausblendbar (view_prefs).
 */
export function DashboardPage() {
  const me = useMe();
  const modules = useModules();
  const queryClient = useQueryClient();
  const [configOpen, setConfigOpen] = useState(false);
  const [hidden, setHidden] = useState<string[]>([]);
  const configRef = useRef<HTMLDivElement>(null);

  const viewPref = useQuery({
    queryKey: ['view-prefs', DASHBOARD_VIEW],
    queryFn: () =>
      api<{ config: { hidden?: string[] } | null }>(
        `/api/v1/view-prefs?entityType=${DASHBOARD_VIEW}`,
      ),
  });

  useEffect(() => {
    const stored = viewPref.data?.config?.hidden;
    if (Array.isArray(stored)) setHidden(stored);
  }, [viewPref.data]);

  const saveHidden = useMutation({
    mutationFn: (next: string[]) =>
      api('/api/v1/view-prefs', {
        method: 'PUT',
        body: { entityType: DASHBOARD_VIEW, config: { hidden: next } },
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['view-prefs', DASHBOARD_VIEW] }),
  });

  const toggleWidget = (key: string): void => {
    const next = hidden.includes(key) ? hidden.filter((entry) => entry !== key) : [...hidden, key];
    setHidden(next);
    saveHidden.mutate(next);
  };

  // Konfig-Menü schließt bei Klick außerhalb
  useEffect(() => {
    if (!configOpen) return;
    const onDown = (event: MouseEvent): void => {
      if (configRef.current && !configRef.current.contains(event.target as Node)) {
        setConfigOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [configOpen]);

  const widgets = useMemo<ResolvedWidget[]>(() => {
    const resolved: ResolvedWidget[] = [
      { key: 'core.activity_feed', title: 'Aktivitäten', span: 1, Component: ActivityFeedWidget },
      { key: 'core.modules', title: 'Module', span: 1, Component: ModuleStatusWidget },
    ];
    for (const frontendModule of modules.data?.frontendModules ?? []) {
      for (const widget of frontendModule.widgets ?? []) {
        resolved.push({
          key: widget.key,
          title: widget.title,
          span: widget.span ?? 1,
          Component: lazy(widget.component),
        });
      }
    }
    resolved.sort((a, b) => (WIDGET_PRIORITY[a.key] ?? 5) - (WIDGET_PRIORITY[b.key] ?? 5));
    return resolved;
  }, [modules.data?.frontendModules]);

  if (me.isLoading) return <Spinner />;

  const visible = widgets.filter((widget) => !hidden.includes(widget.key));
  const mainWidgets = visible.filter((widget) => widget.span >= 2);
  const sideWidgets = visible.filter((widget) => widget.span === 1);

  const renderWidget = (widget: ResolvedWidget) => (
    <Card key={widget.key} title={widget.title}>
      <Suspense fallback={<SkeletonText lines={3} />}>
        <widget.Component />
      </Suspense>
    </Card>
  );

  const today = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="page">
      <div className="dashboard-head">
        <div>
          <h1 className="page__title">
            {greeting()}, {me.data?.user.displayName ?? '…'}
          </h1>
          <p className="page__lead">{today}</p>
        </div>
        <div className="topmenu" ref={configRef}>
          <Button
            variant="subtle"
            size="sm"
            onClick={() => setConfigOpen((open) => !open)}
            aria-label="Dashboard anpassen"
          >
            <Settings2 size={15} aria-hidden /> Anpassen
          </Button>
          {configOpen ? (
            <div className="topmenu__pop" role="menu">
              <div className="topmenu__section">Widgets</div>
              {widgets.map((widget) => (
                <label key={widget.key} className="topmenu__item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!hidden.includes(widget.key)}
                    onChange={() => toggleWidget(widget.key)}
                  />
                  {widget.title}
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-grid__col dashboard-grid__col--main">
          {mainWidgets.map(renderWidget)}
        </div>
        <div className="dashboard-grid__col">{sideWidgets.map(renderWidget)}</div>
      </div>
    </div>
  );
}
